<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <h1 class="gl-page-title">Wallet Management</h1>
        <div class="gl-breadcrumb">
          <router-link to="/" class="gl-breadcrumb-link">Home</router-link>
          <span class="gl-breadcrumb-sep">/</span>
          <span class="gl-breadcrumb-link">Super Admin</span>
          <span class="gl-breadcrumb-sep">/</span>
          <span>Wallets</span>
        </div>
      </div>
    </div>

    <!-- Stats skeleton -->
    <div v-if="loading" class="gl-stats-grid">
      <div v-for="i in 4" :key="i" class="gl-stat-card">
        <div class="gl-skel" style="width:40px;height:40px;border-radius:10px;flex-shrink:0"></div>
        <div style="flex:1">
          <div class="gl-skel" style="height:10px;width:60%;border-radius:6px;margin-bottom:7px"></div>
          <div class="gl-skel" style="height:22px;width:45%;border-radius:6px"></div>
        </div>
      </div>
    </div>

    <!-- Stats -->
    <div v-if="!loading && stats" class="gl-stats-grid">
      <div class="gl-stat-card">
        <div class="gl-stat-icon gl-icon-green">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
        </div>
        <div>
          <div class="gl-stat-label">Total Balance</div>
          <div class="gl-stat-value">RM {{ formatCurrency(stats.total_balance) }}</div>
          <div class="gl-stat-sub">across all wallets</div>
        </div>
      </div>
      <div class="gl-stat-card">
        <div class="gl-stat-icon gl-icon-teal">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
        </div>
        <div>
          <div class="gl-stat-label">Today's Top-ups</div>
          <div class="gl-stat-value">RM {{ formatCurrency(stats.topups_today) }}</div>
          <div class="gl-stat-sub">loaded today</div>
        </div>
      </div>
      <div class="gl-stat-card">
        <div class="gl-stat-icon gl-icon-amber">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
        </div>
        <div>
          <div class="gl-stat-label">Today's Payments</div>
          <div class="gl-stat-value">RM {{ formatCurrency(stats.payments_today) }}</div>
          <div class="gl-stat-sub">spent today</div>
        </div>
      </div>
      <div class="gl-stat-card">
        <div class="gl-stat-icon gl-icon-blue">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        </div>
        <div>
          <div class="gl-stat-label">With Balance</div>
          <div class="gl-stat-value">{{ stats.customers_with_balance || 0 }}</div>
          <div class="gl-stat-sub">customers</div>
        </div>
      </div>
    </div>

    <!-- Filter bar -->
    <div class="gl-filter-bar">
      <div class="gl-search-wrap">
        <svg class="gl-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input type="text" class="gl-search-input" placeholder="Search customer…" v-model="search" @input="debouncedSearch" />
      </div>
    </div>

    <!-- Table card -->
    <div class="gl-card">
      <!-- Skeleton -->
      <template v-if="loading">
        <div class="gl-tbl-skel">
          <div class="gl-tbl-skel-head">
            <div class="gl-skel" style="height:11px;flex:1.5;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;flex:1.2;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;width:100px;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;width:80px;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;width:80px;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;width:64px;border-radius:6px"></div>
          </div>
          <div v-for="i in 7" :key="i" class="gl-skel-row">
            <div style="display:flex;align-items:center;gap:12px;flex:1.5">
              <div class="gl-skel" style="width:38px;height:38px;border-radius:50%;flex-shrink:0"></div>
              <div class="gl-skel" style="height:13px;flex:1;border-radius:6px"></div>
            </div>
            <div style="flex:1.2">
              <div class="gl-skel" style="height:12px;width:80%;border-radius:6px;margin-bottom:6px"></div>
              <div class="gl-skel" style="height:10px;width:55%;border-radius:6px"></div>
            </div>
            <div class="gl-skel" style="width:90px;height:13px;border-radius:6px"></div>
            <div class="gl-skel" style="width:36px;height:20px;border-radius:20px"></div>
            <div class="gl-skel" style="width:80px;height:12px;border-radius:6px"></div>
            <div class="gl-skel" style="width:64px;height:30px;border-radius:8px"></div>
          </div>
        </div>
      </template>

      <!-- Empty -->
      <div v-else-if="customers.length === 0" class="gl-empty-msg">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="opacity:.3;margin-bottom:10px"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
        <div>No customers found</div>
      </div>

      <!-- Desktop table -->
      <div v-else class="gl-tbl-scroll">
        <table class="gl-tbl">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Contact</th>
              <th>Wallet Balance</th>
              <th>Transactions</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="customer in customers" :key="customer?.id">
              <tr v-if="customer">
                <td>
                  <div class="gl-cust-cell">
                    <div class="gl-cust-avatar gl-cust-initial">
                      {{ (customer.name || '?').charAt(0).toUpperCase() }}
                    </div>
                    <router-link :to="`/super-admin/customers/${customer.id}`" class="gl-link gl-td-bold">
                      {{ customer.name || 'No Name' }}
                    </router-link>
                  </div>
                </td>
                <td>
                  <div style="font-size:13px;color:#2a3d3a">{{ customer.phone || '—' }}</div>
                  <div class="gl-td-muted" style="font-size:11.5px;margin-top:2px">{{ customer.email || '—' }}</div>
                </td>
                <td>
                  <span class="gl-wallet-bal">RM {{ formatCurrency(customer.wallet_balance) }}</span>
                </td>
                <td>
                  <span class="gl-badge gl-badge-info">{{ customer.wallet_transactions_count || 0 }}</span>
                </td>
                <td class="gl-td-muted">{{ formatDate(customer.created_at) }}</td>
                <td>
                  <div class="gl-act-group">
                    <router-link :to="`/super-admin/customers/${customer.id}`" class="gl-act-btn gl-act-view" title="View Profile">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    </router-link>
                    <button class="gl-act-btn gl-act-adjust" @click="openAdjustModal(customer)" title="Adjust Balance">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Mobile cards -->
      <div v-if="!loading && customers.length" class="gl-mob-list">
        <template v-for="customer in customers" :key="'m' + customer?.id">
          <div v-if="customer" class="gl-mob-card">
            <div class="gl-mob-top">
              <div class="gl-cust-cell">
                <div class="gl-cust-avatar gl-cust-avatar-lg gl-cust-initial">
                  {{ (customer.name || '?').charAt(0).toUpperCase() }}
                </div>
                <div style="min-width:0">
                  <router-link :to="`/super-admin/customers/${customer.id}`" class="gl-mob-name">
                    {{ customer.name || 'No Name' }}
                  </router-link>
                  <div class="gl-mob-contact">{{ customer.phone || customer.email || '—' }}</div>
                </div>
              </div>
              <div class="gl-mob-bal-wrap">
                <div class="gl-mob-bal-label">Balance</div>
                <div class="gl-wallet-bal" style="font-size:15px">RM {{ formatCurrency(customer.wallet_balance) }}</div>
              </div>
            </div>

            <div class="gl-mob-meta-row-wrap">
              <div class="gl-mob-kv">
                <span class="gl-mob-k">Transactions</span>
                <span class="gl-mob-v">{{ customer.wallet_transactions_count || 0 }}</span>
              </div>
              <div class="gl-mob-kv">
                <span class="gl-mob-k">Joined</span>
                <span class="gl-mob-v">{{ formatDate(customer.created_at) }}</span>
              </div>
            </div>

            <div class="gl-mob-actions">
              <router-link :to="`/super-admin/customers/${customer.id}`" class="gl-btn-view-full">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                View Profile
              </router-link>
              <button class="gl-btn-adjust-full" @click="openAdjustModal(customer)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Adjust Balance
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="!loading && meta.last_page > 1" class="gl-pager">
      <div class="gl-pager-info">Showing {{ (meta.current_page - 1) * meta.per_page + 1 }}–{{ Math.min(meta.current_page * meta.per_page, meta.total) }} of {{ meta.total }}</div>
      <div class="gl-pager-btns">
        <button class="gl-pager-btn" :disabled="meta.current_page === 1" @click="changePage(meta.current_page - 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <button v-for="page in paginationPages" :key="page" class="gl-pager-btn" :class="{ 'gl-pager-btn-active': page === meta.current_page }" @click="changePage(page)">{{ page }}</button>
        <button class="gl-pager-btn" :disabled="meta.current_page === meta.last_page" @click="changePage(meta.current_page + 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    </div>

    <!-- Adjust Balance Modal -->
    <div v-if="showAdjustModal" class="gl-modal-overlay" @click.self="showAdjustModal = false">
      <div class="gl-modal">
        <div class="gl-modal-header">
          <div>
            <h2 class="gl-modal-title">Adjust Wallet Balance</h2>
            <div class="gl-modal-sub">{{ selectedCustomer?.name }}</div>
          </div>
          <button class="gl-modal-close" @click="showAdjustModal = false">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <div class="gl-cur-bal-strip">
            <span class="gl-cur-bal-label">Current Balance</span>
            <span class="gl-cur-bal-val">RM {{ formatCurrency(selectedCustomer?.wallet_balance) }}</span>
          </div>
          <div class="gl-adj-type-row">
            <button class="gl-adj-type-btn" :class="{ active: adjustForm.type === 'add' }" @click="adjustForm.type = 'add'">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add Balance
            </button>
            <button class="gl-adj-type-btn gl-adj-type-deduct" :class="{ active: adjustForm.type === 'deduct' }" @click="adjustForm.type = 'deduct'">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Deduct Balance
            </button>
          </div>
          <div class="gl-form-group">
            <label class="gl-label">Amount (RM) <span class="gl-required">*</span></label>
            <input type="number" v-model="adjustForm.amount" class="gl-input" min="0.01" step="0.01" placeholder="0.00" />
          </div>
          <div class="gl-form-group">
            <label class="gl-label">Reason <span class="gl-required">*</span></label>
            <textarea v-model="adjustForm.reason" class="gl-input gl-textarea" rows="3" placeholder="Enter reason for adjustment"></textarea>
          </div>
        </div>
        <div class="gl-modal-footer">
          <button class="gl-btn-cancel" @click="showAdjustModal = false">Cancel</button>
          <button class="gl-btn-save" :class="adjustForm.type === 'deduct' ? 'gl-btn-save-danger' : ''" @click="adjustBalance" :disabled="adjusting">
            {{ adjusting ? 'Processing…' : (adjustForm.type === 'add' ? 'Add Balance' : 'Deduct Balance') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { superAdminWalletApi } from '@/services/superAdminApi';

export default {
  name: 'WalletDashboard',
  data() {
    return {
      loading: false,
      adjusting: false,
      search: '',
      searchTimeout: null,
      stats: null,
      customers: [],
      selectedCustomer: null,
      showAdjustModal: false,
      adjustForm: { type: 'add', amount: '', reason: '' },
      meta: { current_page: 1, last_page: 1, per_page: 20, total: 0 }
    };
  },
  computed: {
    paginationPages() {
      const pages = [];
      const start = Math.max(1, this.meta.current_page - 2);
      const end = Math.min(this.meta.last_page, this.meta.current_page + 2);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  created() {
    this.fetchStats();
    this.fetchCustomers();
  },
  methods: {
    async fetchStats() {
      try {
        const response = await superAdminWalletApi.getStats();
        if (response.data.success) this.stats = response.data.data;
      } catch (error) { console.error('Failed to fetch stats:', error); }
    },
    async fetchCustomers() {
      this.loading = true;
      try {
        const params = { page: this.meta.current_page, per_page: this.meta.per_page, search: this.search || undefined };
        const response = await superAdminWalletApi.getCustomers(params);
        if (response.data.success) {
          const customersData = response.data.data?.data || response.data.data || [];
          this.customers = (Array.isArray(customersData) ? customersData : []).filter(c => c && c.id);
          if (response.data.meta) this.meta = response.data.meta;
        }
      } catch (error) { console.error('Failed to fetch customers:', error); this.customers = []; }
      finally { this.loading = false; }
    },
    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => { this.meta.current_page = 1; this.fetchCustomers(); }, 300);
    },
    changePage(page) {
      if (page >= 1 && page <= this.meta.last_page) { this.meta.current_page = page; this.fetchCustomers(); }
    },
    openAdjustModal(customer) {
      this.selectedCustomer = customer;
      this.adjustForm = { type: 'add', amount: '', reason: '' };
      this.showAdjustModal = true;
    },
    async adjustBalance() {
      if (!this.adjustForm.amount || !this.adjustForm.reason) { alert('Please fill in all fields'); return; }
      this.adjusting = true;
      try {
        const response = await superAdminWalletApi.adjustBalance(this.selectedCustomer.id, {
          type: this.adjustForm.type,
          amount: parseFloat(this.adjustForm.amount),
          reason: this.adjustForm.reason
        });
        if (response.data.success) {
          this.selectedCustomer.wallet_balance = response.data.data.new_balance;
          this.showAdjustModal = false;
          this.fetchCustomers();
          this.fetchStats();
        }
      } catch (error) {
        console.error('Failed to adjust balance:', error);
        alert(error.response?.data?.message || 'Failed to adjust balance');
      } finally { this.adjusting = false; }
    },
    formatCurrency(value) {
      if (!value) return '0.00';
      return parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' });
    }
  }
};
</script>

<style scoped>
.gl-page { padding: 24px 28px 48px; }
.gl-page-header { margin-bottom: 20px; }
.gl-page-title { font-size: 22px; font-weight: 800; color: #1a2e2b; letter-spacing: -0.4px; margin: 0 0 4px; }
.gl-breadcrumb { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #8a9e9a; }
.gl-breadcrumb-link { color: #900C27; text-decoration: none; }
.gl-breadcrumb-link:hover { text-decoration: underline; }
.gl-breadcrumb-sep { opacity: .5; }

/* ── Stats ── */
.gl-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 16px; }
.gl-stat-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; padding: 18px 20px; display: flex; align-items: center; gap: 14px; }
.gl-stat-icon { width: 42px; height: 42px; border-radius: 11px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-icon-teal  { background: rgba(144,12,39,.12); color: #900C27; }
.gl-icon-blue  { background: rgba(59,130,246,.12); color: #3b82f6; }
.gl-icon-green { background: rgba(22,163,74,.12);  color: #16a34a; }
.gl-icon-amber { background: rgba(245,158,11,.12); color: #d97706; }
.gl-stat-label { font-size: 11px; font-weight: 600; color: #8a9e9a; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 4px; }
.gl-stat-value { font-size: 20px; font-weight: 800; color: #1a2e2b; letter-spacing: -0.5px; margin-bottom: 2px; }
.gl-stat-sub { font-size: 11px; color: #8a9e9a; }

/* ── Filter bar ── */
.gl-filter-bar { display: flex; gap: 10px; margin-bottom: 16px; }
.gl-search-wrap { position: relative; flex: 1; max-width: 340px; }
.gl-search-icon { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: #8a9e9a; pointer-events: none; }
.gl-search-input { width: 100%; padding: 9px 12px 9px 34px; border: 1px solid #e0e7e5; border-radius: 9px; font-size: 13px; color: #2a3d3a; background: #fff; outline: none; transition: border-color .15s; box-sizing: border-box; }
.gl-search-input:focus { border-color: #900C27; }

/* ── Card ── */
.gl-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; overflow: hidden; margin-bottom: 16px; }

/* ── Table ── */
.gl-tbl-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.gl-tbl { width: 100%; border-collapse: collapse; min-width: 560px; }
.gl-tbl thead th { font-size: 11px; font-weight: 700; color: #8a9e9a; text-transform: uppercase; letter-spacing: .4px; padding: 11px 18px; background: #f8faf9; border-bottom: 1px solid #eef0ef; white-space: nowrap; }
.gl-tbl tbody tr { border-bottom: 1px solid #f5f7f6; transition: background .12s; }
.gl-tbl tbody tr:last-child { border-bottom: none; }
.gl-tbl tbody tr:hover { background: #f9fbfa; }
.gl-tbl tbody td { padding: 13px 18px; font-size: 13px; color: #2a3d3a; }
.gl-td-muted { color: #6b7f7b !important; }
.gl-td-bold { font-weight: 700; color: #1a2e2b !important; }
.gl-link { color: #900C27; font-weight: 600; text-decoration: none; }
.gl-link:hover { text-decoration: underline; }

/* ── Customer cell ── */
.gl-cust-cell { display: flex; align-items: center; gap: 10px; }
.gl-cust-avatar { width: 38px; height: 38px; border-radius: 50%; overflow: hidden; flex-shrink: 0; }
.gl-cust-avatar-lg { width: 44px; height: 44px; }
.gl-cust-initial { background: rgba(144,12,39,.1); color: #900C27; font-size: 15px; font-weight: 800; display: flex; align-items: center; justify-content: center; }

/* ── Wallet balance ── */
.gl-wallet-bal { font-weight: 700; color: #16a34a; font-size: 14px; }

/* ── Badges ── */
.gl-badge { display: inline-block; font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 20px; white-space: nowrap; }
.gl-badge-info { background: #dbeafe; color: #1d4ed8; }

/* ── Actions ── */
.gl-act-group { display: flex; gap: 6px; }
.gl-act-btn { width: 30px; height: 30px; border-radius: 7px; border: 1px solid; display: flex; align-items: center; justify-content: center; cursor: pointer; background: transparent; transition: all .15s; text-decoration: none; }
.gl-act-view { border-color: #900C27; color: #900C27; }
.gl-act-view:hover { background: rgba(144,12,39,.08); }
.gl-act-adjust { border-color: #16a34a; color: #16a34a; }
.gl-act-adjust:hover { background: rgba(22,163,74,.08); }

/* ── Skeleton ── */
.gl-skel { background: linear-gradient(90deg, #f0f2f1 25%, #e5e8e7 50%, #f0f2f1 75%); background-size: 200% 100%; animation: gl-shimmer 1.4s infinite; border-radius: 6px; }
.gl-tbl-skel-head { display: flex; align-items: center; gap: 16px; padding: 12px 18px; background: #f8faf9; border-bottom: 1px solid #eef0ef; }
.gl-skel-row { display: flex; align-items: center; gap: 16px; padding: 14px 18px; border-bottom: 1px solid #f5f7f6; }
@keyframes gl-shimmer { to { background-position: -200% 0; } }

/* ── Empty ── */
.gl-empty-msg { padding: 48px 0; text-align: center; font-size: 13px; color: #a0b5b0; display: flex; flex-direction: column; align-items: center; }

/* ── Mobile cards ── */
.gl-mob-list { display: none; }

/* ── Pagination ── */
.gl-pager { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; }
.gl-pager-info { font-size: 12.5px; color: #8a9e9a; }
.gl-pager-btns { display: flex; gap: 4px; }
.gl-pager-btn { min-width: 32px; height: 32px; border: 1px solid #e0e7e5; border-radius: 7px; background: #fff; color: #2a3d3a; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0 6px; transition: all .15s; }
.gl-pager-btn:hover:not(:disabled) { border-color: #900C27; color: #900C27; }
.gl-pager-btn:disabled { opacity: .4; cursor: not-allowed; }
.gl-pager-btn-active { background: #900C27 !important; border-color: #900C27 !important; color: #fff !important; font-weight: 700; }

/* ── Modal ── */
.gl-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center; z-index: 1050; padding: 16px; }
.gl-modal { background: #fff; border-radius: 18px; width: 100%; max-width: 460px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,.18); }
.gl-modal-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 22px 24px 16px; border-bottom: 1px solid #f0f3f2; }
.gl-modal-title { font-size: 17px; font-weight: 800; color: #1a2e2b; margin: 0 0 3px; }
.gl-modal-sub { font-size: 13px; color: #8a9e9a; }
.gl-modal-close { width: 32px; height: 32px; border-radius: 8px; border: 1px solid #eaedec; background: #fff; color: #6b7f7b; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all .15s; }
.gl-modal-close:hover { background: #f5f7f6; color: #1a2e2b; }
.gl-modal-body { padding: 20px 24px; display: flex; flex-direction: column; gap: 14px; }
.gl-modal-footer { padding: 16px 24px 22px; border-top: 1px solid #f0f3f2; display: flex; justify-content: flex-end; gap: 10px; }

/* ── Current balance strip ── */
.gl-cur-bal-strip { display: flex; align-items: center; justify-content: space-between; background: #f8faf9; border: 1px solid #eaedec; border-radius: 10px; padding: 12px 16px; }
.gl-cur-bal-label { font-size: 12px; font-weight: 600; color: #8a9e9a; text-transform: uppercase; letter-spacing: .3px; }
.gl-cur-bal-val { font-size: 16px; font-weight: 800; color: #1a2e2b; }

/* ── Adjustment type toggle ── */
.gl-adj-type-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.gl-adj-type-btn { padding: 10px 12px; border-radius: 9px; border: 1.5px solid #e0e7e5; background: #fff; color: #6b7f7b; font-size: 13px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 7px; transition: all .15s; }
.gl-adj-type-btn.active { border-color: #900C27; background: rgba(144,12,39,.06); color: #900C27; }
.gl-adj-type-deduct.active { border-color: #dc2626; background: rgba(220,38,38,.06); color: #dc2626; }

/* ── Form ── */
.gl-form-group { display: flex; flex-direction: column; gap: 6px; }
.gl-label { font-size: 12.5px; font-weight: 600; color: #2a3d3a; }
.gl-required { color: #dc2626; }
.gl-input { padding: 9px 12px; border: 1px solid #e0e7e5; border-radius: 9px; font-size: 13px; color: #2a3d3a; background: #fff; outline: none; transition: border-color .15s; width: 100%; box-sizing: border-box; }
.gl-input:focus { border-color: #900C27; }
.gl-textarea { resize: vertical; min-height: 72px; }
.gl-btn-cancel { padding: 9px 18px; border: 1px solid #e0e7e5; border-radius: 9px; background: #fff; color: #6b7f7b; font-size: 13px; font-weight: 600; cursor: pointer; }
.gl-btn-cancel:hover { background: #f5f7f6; }
.gl-btn-save { padding: 9px 22px; background: #900C27; color: #fff; border: none; border-radius: 9px; font-size: 13px; font-weight: 700; cursor: pointer; white-space: nowrap; transition: background .15s; }
.gl-btn-save:hover:not(:disabled) { background: #1a5248; }
.gl-btn-save:disabled { opacity: .6; cursor: not-allowed; }
.gl-btn-save-danger { background: #dc2626; }
.gl-btn-save-danger:hover:not(:disabled) { background: #b91c1c; }

/* ── Mobile ── */
@media (max-width: 767px) {
  .gl-page { padding: 14px 16px 40px; }
  .gl-page-header { margin-bottom: 14px; }
  .gl-breadcrumb { display: none; }
  .gl-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
  .gl-stat-card { padding: 14px; gap: 10px; }
  .gl-stat-icon { width: 36px; height: 36px; border-radius: 9px; }
  .gl-stat-value { font-size: 17px; }
  .gl-filter-bar { }
  .gl-search-wrap { max-width: 100%; }

  .gl-tbl-scroll { display: none; }
  .gl-tbl-skel-head { display: none; }
  .gl-mob-list { display: flex; flex-direction: column; }
  .gl-skel-row { padding: 12px 16px; gap: 10px; }

  .gl-mob-card { padding: 14px 16px; border-bottom: 1px solid #f0f3f2; }
  .gl-mob-card:last-child { border-bottom: none; }
  .gl-mob-top { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 10px; }
  .gl-mob-name { font-size: 14px; font-weight: 700; color: #900C27; text-decoration: none; display: block; }
  .gl-mob-name:hover { text-decoration: underline; }
  .gl-mob-contact { font-size: 12px; color: #6b7f7b; margin-top: 2px; }
  .gl-mob-bal-wrap { text-align: right; flex-shrink: 0; }
  .gl-mob-bal-label { font-size: 10.5px; font-weight: 600; color: #8a9e9a; text-transform: uppercase; letter-spacing: .3px; margin-bottom: 2px; }
  .gl-mob-meta-row-wrap { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 14px; padding: 10px 0; border-top: 1px solid #f5f7f6; border-bottom: 1px solid #f5f7f6; margin-bottom: 10px; }
  .gl-mob-kv { display: flex; flex-direction: column; gap: 2px; }
  .gl-mob-k { font-size: 10.5px; font-weight: 600; color: #8a9e9a; text-transform: uppercase; letter-spacing: .3px; }
  .gl-mob-v { font-size: 13px; font-weight: 700; color: #1a2e2b; }
  .gl-mob-actions { display: flex; gap: 8px; }
  .gl-btn-view-full { flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 8px 12px; border: 1px solid #900C27; border-radius: 8px; color: #900C27; font-size: 13px; font-weight: 600; text-decoration: none; }
  .gl-btn-adjust-full { flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 8px 12px; border: 1px solid #16a34a; border-radius: 8px; color: #16a34a; font-size: 13px; font-weight: 600; cursor: pointer; background: transparent; }

  .gl-modal { border-radius: 16px 16px 0 0; position: fixed; bottom: 0; left: 0; right: 0; max-width: 100%; max-height: 92vh; }
  .gl-modal-overlay { align-items: flex-end; padding: 0; }

  .gl-pager { flex-direction: column; align-items: flex-start; gap: 8px; }
}
</style>

<style>
body.dark-only .gl-page-title { color: #e0eded; }
body.dark-only .gl-breadcrumb-link { color: #5a7470; }
body.dark-only .gl-stat-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-stat-label { color: #5a7470; }
body.dark-only .gl-stat-value { color: #e0eded; }
body.dark-only .gl-stat-sub { color: #5a7470; }
body.dark-only .gl-search-input { background: #1e2d2b; border-color: #2a3d3a; color: #c0d4d0; }
body.dark-only .gl-search-input:focus { border-color: #3d9e8c; }
body.dark-only .gl-search-icon { color: #5a7470; }
body.dark-only .gl-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-tbl thead th { background: #16211f; color: #5a7470; border-bottom-color: #2a3d3a; }
body.dark-only .gl-tbl tbody tr { border-bottom-color: #222f2d; }
body.dark-only .gl-tbl tbody tr:hover { background: #1c2f2b; }
body.dark-only .gl-tbl tbody td { color: #c0d4d0; }
body.dark-only .gl-td-muted { color: #5a7470 !important; }
body.dark-only .gl-td-bold { color: #e0eded !important; }
body.dark-only .gl-cust-initial { background: rgba(144,12,39,.2); }
body.dark-only .gl-skel { background: linear-gradient(90deg, #243432 25%, #2a3d3a 50%, #243432 75%); background-size: 200% 100%; }
body.dark-only .gl-tbl-skel-head { background: #16211f; border-bottom-color: #2a3d3a; }
body.dark-only .gl-skel-row { border-bottom-color: #222f2d; }
body.dark-only .gl-empty-msg { color: #5a7470; }
body.dark-only .gl-pager-btn { background: #1e2d2b; border-color: #2a3d3a; color: #c0d4d0; }
body.dark-only .gl-pager-info { color: #5a7470; }
body.dark-only .gl-mob-card { border-bottom-color: #222f2d; }
body.dark-only .gl-mob-name { color: #3d9e8c; }
body.dark-only .gl-mob-contact { color: #5a7470; }
body.dark-only .gl-mob-meta-row-wrap { border-color: #2a3d3a; }
body.dark-only .gl-mob-v { color: #e0eded; }
body.dark-only .gl-modal { background: #1e2d2b; }
body.dark-only .gl-modal-header { border-bottom-color: #2a3d3a; }
body.dark-only .gl-modal-title { color: #e0eded; }
body.dark-only .gl-modal-sub { color: #5a7470; }
body.dark-only .gl-modal-close { background: #1e2d2b; border-color: #2a3d3a; color: #5a7470; }
body.dark-only .gl-modal-close:hover { background: #243432; color: #e0eded; }
body.dark-only .gl-modal-footer { border-top-color: #2a3d3a; }
body.dark-only .gl-label { color: #c0d4d0; }
body.dark-only .gl-input { background: #16211f; border-color: #2a3d3a; color: #c0d4d0; }
body.dark-only .gl-input:focus { border-color: #3d9e8c; }
body.dark-only .gl-cur-bal-strip { background: #16211f; border-color: #2a3d3a; }
body.dark-only .gl-cur-bal-label { color: #5a7470; }
body.dark-only .gl-cur-bal-val { color: #e0eded; }
body.dark-only .gl-adj-type-btn { background: #1e2d2b; border-color: #2a3d3a; color: #5a7470; }
body.dark-only .gl-adj-type-btn.active { border-color: #3d9e8c; background: rgba(61,158,140,.1); color: #3d9e8c; }
body.dark-only .gl-adj-type-deduct.active { border-color: #dc2626; background: rgba(220,38,38,.1); color: #f87171; }
body.dark-only .gl-btn-cancel { background: #1e2d2b; border-color: #2a3d3a; color: #c0d4d0; }
body.dark-only .gl-btn-cancel:hover { background: #243432; }
</style>
